import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integrations } from '@cwos/api-client';
import type { integrations as Api } from '@cwos/api-client';

type ConnectionTestResult = Api.ConnectionTestResult;
/**
 * `GET /integrations/connectors` sends `available` on every connector and
 * `unavailableReason` when it is false, and `Api.ConnectorOverview` now
 * declares both. (It did not, once, and that omission is the whole reason
 * `connectorAvailability` exists as its own function with its own docblock
 * about which polarity to trust when the key is missing.)
 */
type ConnectorOverview = Api.ConnectorOverview;
type CredentialFieldMeta = Api.CredentialFieldMeta;
import { ConfirmAction, NotYetObserved, Pill, SecretField, SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsField, SettingsHeader } from './common.js';
import {
  connectorSubtitle,
  canRetryFirstSync,
  firstSyncActionLabel,
  connectorAvailability,
  historicalSyncCopy,
  isInFlightStatus,
  nextSyncCopy,
  outcomeLabel,
  outcomeTone,
  overallBanner,
  connectorStalenessNote,
  degradedCopy,
  splitCredentialFields,
  statusTone,
  streamCompletenessLabel,
  streamCompletenessTone,
  streamKeyLabel,
  syncFailureCopy,
  withheldBannerNote,
  WITHHELD_CONNECTOR_LABEL,
} from './integrationsLogic.js';
import { RejectedRecordsPanel } from './RejectedRecords.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Integration Center (doc 01 A5.1 Records & sync). The customer-facing surface
 * for the generic connector framework: list every defined connector, connect it
 * with credentials, test the connection, see its PRODUCT-LEVEL state and last
 * successful update, review a read-only import history, and disconnect.
 *
 * Everything shown here is product-level (owner #5050): the API projects the
 * internal state machine and sync-run telemetry onto a small closed vocabulary
 * before it reaches this screen. Credentials are write-only — a stored secret is
 * never read back (ADR 0013), so the form uses {@link SecretField}.
 */
export function RecordsSettings() {
  const { has } = usePermissions();
  const canManage = has('integrations:manage');

  const overview = useQuery({
    queryKey: ['integrations', 'connectors'],
    queryFn: () => integrations.connectors(),
    // Fast poll while anything is in flight (first sync, a queued Sync now, a
    // test) so the lifecycle stages move on screen; slow otherwise. The 60s
    // floor is the same cadence-adjacent freshness the screen has always had.
    refetchInterval: (query) => {
      const connectors = query.state.data ?? [];
      const busy = connectors.some(
        (c) =>
          c.instance != null && (c.instance.syncRunning || isInFlightStatus(c.instance.status)),
      );
      return busy ? 5_000 : 60_000;
    },
  });

  const banner = useMemo(
    () => (overview.data ? overallBanner(overview.data) : null),
    [overview.data],
  );

  // Said next to the banner rather than folded into it: the banner counts
  // INSTANCES, so a connector nobody could ever connect is invisible to it and
  // "No connections yet · Connect a system below" points at a row that refuses.
  const withheldNote = useMemo(
    () => (overview.data ? withheldBannerNote(overview.data) : null),
    [overview.data],
  );

  return (
    <div className="rn-col" style={{ maxWidth: 720 }}>
      <SettingsHeader
        title="Integrations"
        blurb="Connect a records system, see its current state, and manage its credentials."
      />

      <SettingsCard>
        <h2 className="rn-card-title">Connections</h2>
        {overview.isLoading ? (
          <SkeletonSet surface="table" rows={2} />
        ) : overview.isError && !overview.data ? (
          <LoadFailure
            error={overview.error}
            message="We couldn’t load your connections just now."
            onRetry={() => void overview.refetch()}
          />
        ) : banner ? (
          <div style={{ marginTop: 'var(--space-10)' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--space-12)',
              }}
            >
              <span style={{ fontWeight: 600 }}>{banner.label}</span>
              <Pill variant={banner.tone} dot>
                {banner.label}
              </Pill>
            </div>
            <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
              {banner.message}
            </p>
            {withheldNote ? (
              <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }} role="status">
                {withheldNote}
              </p>
            ) : null}
          </div>
        ) : null}
      </SettingsCard>

      {/* `overview.data ?? []` rendered nothing at all when the read failed:
          the Connections card body was blank and no connector card appeared, so
          an operator saw a page that said their organisation has no connectors.
          The top-level guard covers `isError && !data`; this covers the
          stale-refresh case, where rows exist and the refresh did not. */}
      {overview.isError && overview.data !== undefined ? (
        <p className="rn-muted" role="status">
          These connectors are the last ones we could read. A refresh just failed, so their state
          may have moved on.
        </p>
      ) : null}

      {(overview.data ?? []).map((connector) => (
        <ConnectorCard key={connector.key} connector={connector} canManage={canManage} />
      ))}
    </div>
  );
}

function ConnectorCard({
  connector,
  canManage,
}: {
  connector: ConnectorOverview;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const { instance } = connector;
  const { secretFields, configFields } = useMemo(
    () => splitCredentialFields(connector.credentialSchema),
    [connector.credentialSchema],
  );

  const connected = instance != null && instance.hasCredentials;

  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<Record<string, string>>(() => ({
    ...(instance?.config ?? {}),
  }));
  const [replacing, setReplacing] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // PH-UX-004: Disconnect is the only operation that destroys a stored
  // credential (see the comment on `availability` below) and used to fire on
  // one click. The confirmation is a client-side gate only — it does not (and
  // must not) make Disconnect refusable server-side; see the withheld-connector
  // comment below for why that stays deliberate.
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['integrations', 'connectors'] });
    void qc.invalidateQueries({ queryKey: ['integrations', 'history', connector.key] });
  };

  // Connect & Sync = create the instance (if new) → store the secret → test
  // (validation before anything claims success) → kick off the FULL first-sync
  // lifecycle (historical backfill → reconciliation → completion → recurring
  // sync). All server-side; the secret leaves this component only through
  // storeCredentials. A failed test stops here — no first sync is queued on a
  // credential the vendor refused.
  const connect = useMutation({
    mutationFn: async () => {
      const id = instance?.id ?? (await integrations.create(connector.key, config)).id;
      await integrations.storeCredentials(id, { credentials: secrets, config });
      const result = await integrations.test(id);
      if (!result.ok) {
        throw new Error(result.message ?? 'Connection test failed.');
      }
      return integrations.connectSync(id);
    },
    onSuccess: () => {
      setTestResult(null);
      setSecrets({});
      invalidate();
    },
    onError: (err: unknown) =>
      setFormError(err instanceof Error ? err.message : 'Connection failed.'),
  });

  const replace = useMutation({
    mutationFn: async () => {
      if (!instance) throw new Error('not connected');
      await integrations.rotateCredentials(instance.id, { credentials: secrets, config });
      return integrations.test(instance.id);
    },
    onSuccess: (result) => {
      setTestResult(result);
      setSecrets({});
      setReplacing(false);
      invalidate();
    },
    onError: (err: unknown) => setFormError(err instanceof Error ? err.message : 'Update failed.'),
  });

  const test = useMutation({
    mutationFn: () => {
      if (!instance) throw new Error('not connected');
      return integrations.test(instance.id);
    },
    onSuccess: (result) => {
      setTestResult(result);
      invalidate();
    },
  });

  // Resume = ONE server operation: enable → validate → settled status
  // (connected / authentication required / not connected). The response is
  // already settled, so the card never shows a "Validating…" it cannot leave.
  const resume = useMutation({
    mutationFn: () => {
      if (!instance) throw new Error('not connected');
      return integrations.enable(instance.id);
    },
    onSuccess: () => {
      setTestResult(null);
      invalidate();
    },
    // A proxy timeout (nginx reads /api for 60s; a Ritten test can take ~50s)
    // does not stop the server settling the row, so refetch and show its state.
    onError: (err: unknown) => {
      setFormError(err instanceof Error ? err.message : 'Resume failed.');
      invalidate();
    },
  });

  const disconnect = useMutation({
    mutationFn: () => {
      if (!instance) throw new Error('not connected');
      return integrations.disconnect(instance.id);
    },
    onSuccess: () => {
      setConfirmingDisconnect(false);
      setTestResult(null);
      setConfig({});
      invalidate();
    },
  });

  // Sync now = enqueue ONE incremental sync (the server never runs it inside
  // this request). A 409 (an import is already running) is a normal answer,
  // not an error: the button is disabled on `syncRunning`, and this catch just
  // surfaces the run-state the server knows about while the refetch catches up.
  const syncNow = useMutation({
    mutationFn: () => {
      if (!instance) throw new Error('not connected');
      return integrations.sync(instance.id);
    },
    onSuccess: () => {
      setFormError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      if (err instanceof Error && /already running/i.test(err.message)) {
        setFormError('An import is already running — it will show up here shortly.');
      } else {
        setFormError(err instanceof Error ? err.message : 'Sync could not be started.');
      }
    },
  });

  // Retry sync = re-press the FIRST sync for an installation whose historical
  // import has not completed: the same durable connect-sync job, re-enqueued
  // (the singleton key dedupes it against a live one). This is the ONLY way
  // back after a failed first sync short of disconnecting and setting the
  // whole connection up again — `canRetryFirstSync` gates the states the
  // server accepts the press from.
  const retrySync = useMutation({
    mutationFn: () => {
      if (!instance) throw new Error('not connected');
      return integrations.connectSync(instance.id);
    },
    onSuccess: () => {
      setFormError(null);
      invalidate();
    },
    onError: (err: unknown) =>
      setFormError(err instanceof Error ? err.message : 'Historical import could not be started.'),
  });

  const requiredMissing =
    secretFields.some((f) => f.required && !secrets[f.key]) ||
    configFields.some((f) => f.required && !config[f.key]?.trim());

  /**
   * WHETHER THIS CONNECTOR CAN BE CONNECTED AT ALL — and, until this was read,
   * a question the screen never asked.
   *
   * The server withholds a connector by refusing `createIntegration`,
   * `storeCredentials`, `rotateCredentials`, `testConnection`, `enable` and
   * `runSyncOnce` with a 409 (`service.ts:184`). Every button below except
   * Disconnect is one of those calls, so on a withheld connector this card was
   * three dead buttons and a form that took a live vendor secret before saying
   * so. Disconnect is deliberately NOT refused server-side — it is the only
   * operation that destroys a stored credential — so it is the only one kept.
   */
  const availability = connectorAvailability(connector);
  const withheld = availability.kind === 'withheld' ? availability.reason : null;

  // Given the AVAILABILITY, not just the level: on a withheld connector
  // "reconnect to resume updates" names the one action the server answers 409
  // to. See `connectorStalenessNote`.
  const staleNote = instance ? connectorStalenessNote(instance.staleness, availability) : null;
  // The first-sync honesty lines, derived from the server's own product-level
  // fields — never guessed client-side.
  const historicalCopy = instance
    ? historicalSyncCopy(
        instance.historicalSyncComplete,
        instance.historicalSyncPhase,
        // A failure category exists only after a run that actually happened, so
        // it is the honest signal for "this was attempted" vs "never touched".
        instance.syncErrorCategory !== null,
      )
    : null;
  const failureCopy = instance ? syncFailureCopy(instance.syncErrorCategory) : null;
  // PH-UX-001 part 2: the degraded card's only explanatory line. Withheld
  // connectors are excluded — their own note already says why the card is
  // inert, and two explanations would contradict each other.
  const degradedLine =
    instance && !withheld ? degradedCopy(instance.status, instance.staleness) : null;
  const nextSyncLine = instance
    ? nextSyncCopy(instance.syncCadenceMinutes, instance.nextScheduledSyncAt, (iso) =>
        new Date(iso).toLocaleTimeString(),
      )
    : null;
  const busy =
    connect.isPending ||
    replace.isPending ||
    test.isPending ||
    resume.isPending ||
    disconnect.isPending ||
    syncNow.isPending ||
    retrySync.isPending;

  return (
    <SettingsCard>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-12)',
        }}
      >
        <div>
          <h2 className="rn-card-title">{connector.displayName}</h2>
          <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0', fontSize: 13 }}>
            {connectorSubtitle(connector.category, connector.capabilities)}
          </p>
        </div>
        {/* A withheld connector's pill is never the status pill. "Not
            connected" invites the reader to connect it, and `statusLabel` on a
            withheld instance describes a state machine that is not going to
            move. */}
        <Pill variant={withheld ? 'unsure' : instance ? statusTone(instance.status) : 'plain'} dot>
          {withheld ? WITHHELD_CONNECTOR_LABEL : instance ? instance.statusLabel : 'Not connected'}
        </Pill>
      </div>

      {/* CORE-AUD-220: the pill speaks for the connection; this line speaks for
          what Needs Attention holds open about it, so the two screens agree. */}
      {!withheld && instance?.openIssues && instance.openIssues.total > 0 ? (
        <p role="status" className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
          {instance.openIssues.total === 1
            ? '1 open issue in Needs Attention'
            : `${instance.openIssues.total} open issues in Needs Attention`}
          {instance.openIssues.critical > 0 ? ` (${instance.openIssues.critical} critical)` : ''}
        </p>
      ) : null}

      {/* The withholding, said before anything else on the card. It is the
          reason every control below is missing, and a card that hid the
          controls without saying why would read as a permission problem the
          operator could go and fix. */}
      {withheld ? (
        <p
          role="status"
          style={{ margin: 'var(--space-12) 0 0', color: 'var(--color-unsure-deep)' }}
        >
          {withheld}
        </p>
      ) : null}

      {connected && instance ? (
        <div style={{ marginTop: 'var(--space-12)' }}>
          <p className="rn-muted" style={{ margin: 0 }}>
            Last successful update:{' '}
            {instance.lastSyncedAt ? new Date(instance.lastSyncedAt).toLocaleString() : 'not yet'}
          </p>
          {staleNote ? (
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              {staleNote}
            </p>
          ) : null}

          {/* THE TWO HONESTY LINES the old card did not have: is all my
              history in (and if not, what phase), and when the next regular
              update lands. The cadence text comes from the same cron the
              server's durable dispatcher fires on, so it can never promise
              more than the API actually does. */}
          {historicalCopy ? (
            <p role="status" className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
              {historicalCopy}
            </p>
          ) : null}
          {instance.syncRunning ? (
            <p role="status" className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              An import is running right now — this page will update as it lands.
            </p>
          ) : null}
          {degradedLine ? (
            <p role="status" className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              {degradedLine}
            </p>
          ) : null}
          {failureCopy ? (
            <p
              role="status"
              style={{
                margin: 'var(--space-6) 0 0',
                color: 'var(--color-urgent-deep)',
                fontSize: 13,
              }}
            >
              {failureCopy}
            </p>
          ) : null}
          {nextSyncLine ? (
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              {nextSyncLine}
            </p>
          ) : null}

          {testResult ? (
            <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
              {testResult.ok ? '✓ ' : '⚠ '}
              {testResult.message ??
                (testResult.ok ? 'Connection succeeded.' : 'Connection test failed.')}
              {/*
               * PH-UX-001 part 5. "✓ Connection succeeded." was rendering on
               * the same card as the attention pill and the rows saying this
               * run's totals could not be confirmed. Both sentences are true
               * and they answer DIFFERENT questions — the test opens a
               * credentialled connection, it does not count records — but read
               * together the green tick reads as "so the data is fine too".
               * The caveat is only appended where that misreading is available:
               * a PASSING test on an instance that is flagged.
               */}
              {testResult.ok && instance.needsAttention
                ? ' This checks the credentials and the connection only — it does not check' +
                  ' whether the records that came in are complete.'
                : null}
            </p>
          ) : null}

          {canManage ? (
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-8)',
                marginTop: 'var(--space-12)',
                flexWrap: 'wrap',
              }}
            >
              {/* `testConnection` and `rotateCredentials` are both refused for a
                  withheld connector, so offering them is offering a 409.
                  Disconnect below is not refused and stays: taking a stored
                  credential back must always be possible. */}
              {withheld ? null : (
                <>
                  {instance.status === 'paused' ? (
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                      disabled={busy}
                      onClick={() => {
                        setFormError(null);
                        resume.mutate();
                      }}
                    >
                      {resume.isPending ? 'Resuming…' : 'Resume'}
                    </button>
                  ) : null}
                  {/* Sync now: ENQUEUES a safe incremental job. Only offered
                      once the first sync is complete — an incremental "now"
                      during the historical walk would be a second vendor read
                      racing the first — and disabled while one is running. */}
                  {instance.historicalSyncComplete ? (
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                      disabled={busy || instance.syncRunning}
                      onClick={() => {
                        setFormError(null);
                        syncNow.mutate();
                      }}
                    >
                      {syncNow.isPending ? 'Queueing…' : 'Sync now'}
                    </button>
                  ) : null}
                  {/* The historical import: the path for a one-time history
                      import that has not completed — never started, or started
                      and failed. Re-presses the SAME durable first-sync job —
                      hidden once history is in, while an import is running, and
                      in the states the server refuses the press from (see
                      `canRetryFirstSync`). The LABEL distinguishes the two
                      cases: "Retry sync" on an installation whose history has
                      never been imported named a second attempt at something
                      that never happened, so the card above ("Historical sync
                      has not run yet") described a missing import beside a
                      button nobody could read as starting one. See
                      `firstSyncActionLabel`. */}
                  {canRetryFirstSync(instance) ? (
                    <button
                      type="button"
                      className="rn-btn-quiet"
                      disabled={busy}
                      onClick={() => {
                        setFormError(null);
                        retrySync.mutate();
                      }}
                    >
                      {retrySync.isPending ? 'Queueing…' : firstSyncActionLabel(instance)}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rn-btn-quiet"
                    disabled={busy}
                    onClick={() => test.mutate()}
                  >
                    {test.isPending ? 'Testing…' : 'Test connection'}
                  </button>
                  <button
                    type="button"
                    className="rn-btn-quiet"
                    disabled={busy}
                    onClick={() => {
                      setReplacing((v) => !v);
                      setFormError(null);
                    }}
                  >
                    {replacing ? 'Cancel' : 'Replace credentials'}
                  </button>
                </>
              )}
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={busy}
                onClick={() => setConfirmingDisconnect(true)}
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          ) : null}

          {/*
           * PH-UX-004: Disconnect destroys a stored credential and used to fire
           * on one click with no confirmation. Credentials are write-only
           * (AGENTS.md §7 — never returned after creation), so a mistaken press
           * means going back to the vendor to reconnect, not a self-service
           * undo. This is a CLIENT-SIDE gate only: Disconnect stays deliberately
           * un-refusable server-side even on a withheld connector (see
           * `availability` above and PH-UX-001) — this adds a confirmation, it
           * does not add a refusal.
           */}
          <ConfirmAction
            open={confirmingDisconnect}
            title={`Disconnect ${connector.displayName}?`}
            consequence={`This deletes the stored ${connector.displayName} credential — we do not keep a copy of it. To reconnect, you will need to get a new credential from ${connector.displayName} and its full history will import again, which can take a while. Records already imported are kept.`}
            confirmLabel="Disconnect"
            pendingLabel="Disconnecting…"
            pending={disconnect.isPending}
            onCancel={() => setConfirmingDisconnect(false)}
            onConfirm={() => disconnect.mutate()}
          />

          {formError ? (
            <p
              style={{
                color: 'var(--color-urgent-deep)',
                fontSize: 13,
                marginTop: 'var(--space-8)',
              }}
            >
              {formError}
            </p>
          ) : null}

          {replacing && canManage && !withheld ? (
            <div style={{ marginTop: 'var(--space-12)' }}>
              <CredentialFields
                secretFields={secretFields}
                configFields={configFields}
                secrets={secrets}
                config={config}
                stored={instance.hasCredentials}
                disabled={replace.isPending}
                onSecret={(k, v) => setSecrets((s) => ({ ...s, [k]: v }))}
                onConfig={(k, v) => setConfig((c) => ({ ...c, [k]: v }))}
              />
              {formError ? (
                <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }}>{formError}</p>
              ) : null}
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={replace.isPending || requiredMissing}
                onClick={() => {
                  setFormError(null);
                  replace.mutate();
                }}
              >
                {replace.isPending ? 'Saving…' : 'Save & test'}
              </button>
            </div>
          ) : null}

          <ConnectorStreams connectorKey={connector.key} integrationId={instance.id} />
          <ConnectorHistory connectorKey={connector.key} integrationId={instance.id} />
        </div>
      ) : (
        <div style={{ marginTop: 'var(--space-12)' }}>
          {withheld ? (
            /* No form and no Connect. The sentence above already said why; what
               must not happen here is a secret field that accepts a live vendor
               credential on its way to a refusal. */
            <p className="rn-muted" style={{ margin: 0 }}>
              There is nothing to set up here yet. We will make this connection available when it
              can actually be completed.
            </p>
          ) : canManage ? (
            <>
              <CredentialFields
                secretFields={secretFields}
                configFields={configFields}
                secrets={secrets}
                config={config}
                stored={false}
                disabled={connect.isPending}
                onSecret={(k, v) => setSecrets((s) => ({ ...s, [k]: v }))}
                onConfig={(k, v) => setConfig((c) => ({ ...c, [k]: v }))}
              />
              {testResult && !testResult.ok ? (
                <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }}>
                  {testResult.message ?? 'Connection test failed.'}
                </p>
              ) : null}
              {formError ? (
                <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }}>{formError}</p>
              ) : null}
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={connect.isPending || requiredMissing}
                onClick={() => {
                  setFormError(null);
                  connect.mutate();
                }}
              >
                {connect.isPending ? 'Connecting…' : 'Connect & Sync'}
              </button>
              {/* What the button does, said in one line: it is NOT merely
                  "saving the key" — it validates the credential, then imports
                  the whole history, then keeps updating automatically. */}
              <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0', fontSize: 13 }}>
                Connect &amp; Sync validates these credentials, imports everything from your
                source’s history, and then keeps the records updated automatically.
              </p>
            </>
          ) : (
            <p className="rn-muted" style={{ margin: 0 }}>
              This connection isn’t set up yet. Ask an org admin to connect it.
            </p>
          )}
        </div>
      )}
    </SettingsCard>
  );
}

function CredentialFields({
  secretFields,
  configFields,
  secrets,
  config,
  stored,
  disabled,
  onSecret,
  onConfig,
}: {
  secretFields: CredentialFieldMeta[];
  configFields: CredentialFieldMeta[];
  secrets: Record<string, string>;
  config: Record<string, string>;
  stored: boolean;
  disabled: boolean;
  onSecret: (key: string, value: string) => void;
  onConfig: (key: string, value: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-12)', marginBottom: 'var(--space-12)' }}>
      {configFields.map((field) => (
        <SettingsField key={field.key} label={field.label} hint={field.helpText}>
          <input
            type="text"

            value={config[field.key] ?? ''}
            disabled={disabled}
            onChange={(e) => onConfig(field.key, e.target.value)}
          />
        </SettingsField>
      ))}
      {secretFields.map((field) => (
        <SecretField
          key={field.key}
          label={field.label}
          value={secrets[field.key] ?? ''}
          stored={stored}
          disabled={disabled}
          onChange={(v) => onSecret(field.key, v)}
        />
      ))}
    </div>
  );
}

/**
 * Per-stream health (PH-ADV-005).
 *
 * `integration.status` — the pill at the top of this card — is one word about
 * the whole connection, derived from the PRIMARY (encounter) stream. It is not
 * a lie, but it is not the whole truth either: a staff, client, or schedule
 * read can fail completely, on every run, while that pill still says "Synced"
 * — the shape of the incident PH-ADV-005 is filed against. `GET
 * /integrations/:id/streams` has always recorded the per-stream answer;
 * `integrations.streams()` is the first caller of it from this screen, so an
 * operator can see WHICH stream and since when, not only "connected".
 */
function ConnectorStreams({
  connectorKey,
  integrationId,
}: {
  connectorKey: string;
  integrationId: string;
}) {
  const streams = useQuery({
    queryKey: ['integrations', 'streams', connectorKey],
    queryFn: () => integrations.streams(integrationId),
  });

  if (streams.isLoading) {
    return (
      <div style={{ marginTop: 'var(--space-16)' }}>
        <span className="rn-muted" style={{ fontWeight: 600 }}>
          Stream status
        </span>
        <SkeletonSet surface="table" rows={2} />
      </div>
    );
  }
  // Silent on error or an empty list: this is a supplementary detail panel,
  // and the connector's own status pill and history below already carry the
  // primary signal — a missing stream list must never read as "nothing is
  // wrong" nor block the rest of the card from rendering.
  if (streams.isError || !streams.data || streams.data.length === 0) return null;

  return (
    <div style={{ marginTop: 'var(--space-16)' }}>
      <span className="rn-muted" style={{ fontWeight: 600 }}>
        Stream status
      </span>
      <ul
        style={{
          listStyle: 'none',
          margin: 'var(--space-8) 0 0',
          padding: 0,
          display: 'grid',
          gap: 'var(--space-6)',
        }}
      >
        {streams.data.map((s) => (
          <li
            key={s.streamKey}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-12)',
            }}
          >
            <span style={{ fontSize: 13 }}>{streamKeyLabel(s.streamKey)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)' }}>
              <Pill variant={streamCompletenessTone(s.completeness)}>
                {streamCompletenessLabel(s.completeness)}
              </Pill>
              <span className="rn-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {s.lastSuccessAt ? new Date(s.lastSuccessAt).toLocaleString() : 'Never'}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Recent imports, and — for a run that left records behind — WHICH records.
 *
 * `recordsRejected` used to sit unread on every entry while the count was
 * spoken only inside `entry.summary`. The count was the whole of the answer an
 * operator could reach from a screen: the route that names the records existed
 * and nothing called it. `RejectedRecordsPanel` is that call, and it is
 * attached to the entry the count belongs to rather than to the connector, so
 * "which records" always means "which records, on this run".
 */
function ConnectorHistory({
  connectorKey,
  integrationId,
}: {
  connectorKey: string;
  integrationId: string;
}) {
  const history = useQuery({
    queryKey: ['integrations', 'history', connectorKey],
    queryFn: () => integrations.history(integrationId),
  });

  return (
    <div style={{ marginTop: 'var(--space-16)' }}>
      <span className="rn-muted" style={{ fontWeight: 600 }}>
        Recent imports
      </span>
      {history.isLoading ? (
        <SkeletonSet surface="table" rows={2} />
      ) : history.isError ? (
        <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
          We couldn’t load the import history just now.
        </p>
      ) : !history.data || history.data.length === 0 ? (
        <div style={{ marginTop: 'var(--space-8)' }}>
          <NotYetObserved from="the first import runs" />
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 'var(--space-8) 0 0',
            padding: 0,
            display: 'grid',
            gap: 'var(--space-8)',
          }}
        >
          {history.data.map((entry) => (
            <li
              key={entry.id}
              style={{
                borderTop: '1px solid var(--color-line)',
                paddingTop: 'var(--space-8)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--space-12)',
                }}
              >
                <div>
                  <Pill variant={outcomeTone(entry.outcome)}>{outcomeLabel(entry.outcome)}</Pill>{' '}
                  <span className="rn-muted" style={{ fontSize: 13 }}>
                    {entry.summary}
                  </span>
                </div>
                <span className="rn-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {new Date(entry.startedAt).toLocaleString()}
                </span>
              </div>
              {/* Driven by the SERVER's count, not by the summary sentence: a
                  run with a non-zero `recordsRejected` is a run whose records
                  can be named, whatever wording the projection chose. */}
              {entry.recordsRejected > 0 ? (
                <RejectedRecordsPanel integrationId={integrationId} runId={entry.id} />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
